import { InputField } from '@eventbrite/eds-input-field';
import { formatNumber } from '@eventbrite/intl';
import React, {
    ChangeEventHandler,
    FunctionComponent,
    KeyboardEventHandler,
    useState,
} from 'react';

type OnChangeHandlerParameter = Parameters<
    ChangeEventHandler<HTMLInputElement>
>[0]['target']['value'];

export interface TicketDonationFieldProps {
    /**
     * How the currency is formatted
     */
    currencyFormat: string | null;
    /**
     * Symbol for the currency prefix or suffix
     */
    currencySymbol: string | null;
    /**
     * ID of the ticket to be passed up along with the selected value in the callback
     */
    id: string;
    /**
     * Whether the currency symbol is prefix or suffix
     */
    isCurrencySuffix: boolean;
    /**
     * Whether the input is disabled
     */
    isDisabled: boolean;
    /**
     * Callback to be executed on every input change
     */
    onChange: (newValue: OnChangeHandlerParameter) => void;
    /**
     * Amount on the donation ticket
     */
    amount: number | null;
}

const getDisplayValue = ({
    amount,
    currencyFormat,
    shouldFormatInputNumber,
    value,
}: {
    amount: number | null;
    currencyFormat: string | null;
    shouldFormatInputNumber: boolean;
    value: OnChangeHandlerParameter | null;
}) => {
    if ((amount ?? -1) < 0) {
        return null;
    }

    return shouldFormatInputNumber
        ? formatNumber(amount, currencyFormat)
        : value;
};

export const TicketDonationField: FunctionComponent<
    TicketDonationFieldProps
> = ({
    currencyFormat,
    amount,
    onChange,
    currencySymbol,
    id,
    isCurrencySuffix,
    isDisabled,
}) => {
    const [value, setValue] = useState<OnChangeHandlerParameter | null>(null);
    const [shouldFormatInputNumber, setShouldFormatInputNumber] =
        useState(true);

    const formattedPlaceholder = formatNumber(0, currencyFormat);

    const displayValue = getDisplayValue({
        shouldFormatInputNumber,
        value,
        amount,
        currencyFormat,
    });

    const handleOnFocus = () => {
        setShouldFormatInputNumber(false);
    };

    const handleOnBlur = () => {
        setShouldFormatInputNumber(true);
    };

    const handleOnChange = (newValue: OnChangeHandlerParameter) => {
        setValue(newValue);
        onChange(newValue);
    };

    // Block submit action when user clicks enter as user should only be able to submit form when they click "Checkout"
    const handleOnKeyPress: KeyboardEventHandler<HTMLInputElement> = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
        }
    };

    const dataAutomation = `ticket-input-field-${id}`;
    const suffixProp = isCurrencySuffix
        ? { suffix: currencySymbol }
        : { prefix: currencySymbol };

    return (
        <InputField
            id={id}
            label="donation-input-field"
            style="basic"
            onChange={handleOnChange}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
            onKeyPress={handleOnKeyPress}
            placeholder={formattedPlaceholder}
            value={displayValue}
            disabled={isDisabled}
            data-automation={dataAutomation}
            data-testid="ticket-donation-list-field"
            {...suffixProp}
        />
    );
};

export default TicketDonationField;
